import { useEffect, useRef, useMemo } from 'react';

import { Skeleton, Button, ErrorState } from '@/shared';
import { t } from '@/i18n';
import type { I18nKey } from '@/i18n';

import type { FinancialCalendarEvent, FinancialEventImpact } from '@/entities/financial-calendar';
import { useFinancialCalendarDailyCountsQuery, useFinancialCalendarInfiniteQuery } from '../model/query';
import {
  VALID_IMPACTS,
  findDailyCount,
  formatEventDateHeading,
  getEmptyFilterAxes,
  groupEventsByDate,
  hasActiveCalendarFilter,
  isSameImpactSet,
  resolveExpandableTotal,
  selectedImpactDailyCount,
  shouldShowDailyCountNotice,
} from '../model/utils';
import type { CalendarFilter } from '../model/types';
import { CalendarEmptyState } from './CalendarEmptyState';
import { CalendarFilterAxisEmpty } from './CalendarFilterAxisEmpty';
import { EventCard } from './EventCard';
import { resolveErrorDisplay } from '@/shared/lib/query/error-display';
import { useBackgroundErrorToast } from '@/shared/hooks/useBackgroundErrorToast';

interface EventListProps {
  filter: CalendarFilter;
  selectedEventId?: number | null;
  onEventSelect: (event: FinancialCalendarEvent) => void;
  /** TRAD-2071: 날짜 헤더의 "전체 M건 보기" 클릭 시 전역 중요도 필터를 전체로 확장 */
  onExpandToAll?: () => void;
  /** TRAD-3725 — 빈 상태 «필터 없음» 판정의 중요도 기준(기본 DEFAULT_IMPACTS). 일자 페이지 = 전체. */
  pristineImpacts?: FinancialEventImpact[];
  /** TRAD-3725 — 필터 없이 0건일 때 문구 키(기본 «선택한 기간에…»). */
  emptyPeriodTitleKey?: I18nKey;
}

export interface DateGroupHeaderProps {
  date: string;
  /** 현재 필터(주요만 등)로 이미 화면에 표시 중인 이 날짜의 건수 */
  shownCount: number;
  /** 이 날짜의 impact 무관 전체 건수. 백엔드 dailyCounts 미도입 시 undefined(고지 생략) */
  totalCount?: number;
  /** true면 impacts가 VALID_IMPACTS 전체가 아님 = 숨겨진 이벤트가 있을 수 있음 */
  isFiltered: boolean;
  onExpandToAll?: () => void;
}

// export: 순수 판정 로직(shouldShowDailyCountNotice)과 별개로 렌더링 자체도 단위 테스트하기 위해 노출
export const DateGroupHeader = ({ date, shownCount, totalCount, isFiltered, onExpandToAll }: DateGroupHeaderProps) => {
  // TRAD-2752 — 하드코딩 요일 배열(dayNames)·년/월/일 템플릿 제거, Intl.DateTimeFormat으로
  // 교체. TRAD-2917 — 이 포맷이 3사 정합 게이트에서 "날짜 표기 정본"으로 확정돼
  // `formatEventDateHeading`(model/utils.ts)으로 승격됐다. 로직은 그대로 옮긴 것뿐이라
  // 출력은 바이트 단위로 동일하다(회귀 없음) — 상세 패널/드로어도 이제 이 함수를 쓴다.
  const label = formatEventDateHeading(date);

  // TRAD-2071: 정보 비대칭 해소 원칙 — 필터로 걸러진 상태일 때만 "숨겨진 나머지도 볼 수 있다"를 고지.
  const showNotice = shouldShowDailyCountNotice(isFiltered, shownCount, totalCount);

  return (
    <div className="flex items-center justify-between gap-2 pt-4 pb-2 first:pt-0 border-b border-border">
      <h3 className="text-sm font-semibold text-muted-foreground">{label}</h3>
      {showNotice && (
        <p className="text-xs text-muted-foreground whitespace-nowrap">
          {t('financial_calendar.date_header.primary_count', { n: String(shownCount) })}
          <span className="mx-1">·</span>
          <Button
            type="button"
            variant="link"
            onClick={onExpandToAll}
            className="h-auto p-0 text-brand-text hover:underline"
          >
            {t('financial_calendar.date_header.view_all_count', { m: String(totalCount) })}
          </Button>
        </p>
      )}
    </div>
  );
};

const SkeletonEventCard = () => (
  <div className="rounded-lg border border-border p-3 space-y-2 animate-pulse">
    <div className="flex items-center gap-2">
      <Skeleton className="h-3 w-10" />
      <Skeleton className="h-3 w-6" />
      <Skeleton className="h-3 w-16 ml-auto" />
    </div>
    <Skeleton className="h-4 w-3/4" />
    <div className="flex gap-3">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-3 w-20" />
    </div>
  </div>
);

// TRAD-2157 — 이 화면이 장애 표시의 **기준 화면**이므로, 구현을 공용으로 올리고 여기서도 그것을 쓴다.
// 원래 이 자리에 있던 `⚠️` 이모지 아이콘은 감사 SoT-14("이모지를 아이콘으로 쓰지 않는다")가
// 결함으로 지목한 것이라(W-06 / TRAD-2106) lucide 로 함께 교체했다 — 기준 화면이 결함을 들고
// 있으면 복제본만 고쳐도 "어느 쪽을 따라야 하는가"가 갈린다. 문구도 하드코딩 → 카탈로그.
// TRAD-2107(2-b) — 승격된 `shared/ui/ErrorState`와 이름이 같아지면서, size="md"만 고정해
// 감싸던 로컬 래퍼가 자기 자신을 재귀 호출하는 이름 충돌이 됐다. 래퍼를 제거하고 호출부에서
// 바로 size="md"를 넘긴다 — 렌더 결과는 이전과 동일(바이트 단위로 같은 props).

export const EventList = ({
  filter,
  selectedEventId,
  onEventSelect,
  onExpandToAll,
  pristineImpacts,
  emptyPeriodTitleKey,
}: EventListProps) => {
  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useFinancialCalendarInfiniteQuery(filter);

  // Scroll to top when filter changes so user sees the refreshed list from the beginning
  const filterSig = `${filter.from}|${filter.to}|${filter.categories.join(',')}|${filter.countries.join(',')}|${filter.impacts.join(',')}`;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filterSig]);

  // Flatten all pages — filtering is done server-side
  const allEvents = useMemo(() => data?.pages.flatMap(p => p.content) ?? [], [data]);

  const grouped = useMemo(() => groupEventsByDate(allEvents), [allEvents]);
  // TRAD-3656 — 재조회 실패가 보이던 이벤트 목록을 지우지 않게(데이터 있으면 토스트). 🔴 아래 조기 반환보다 «앞»에서 훅 호출.
  const errorDisplay = resolveErrorDisplay({ isError, hasData: grouped.length > 0 });
  useBackgroundErrorToast(errorDisplay === 'background');

  // TRAD-2071: 날짜별 {highCount,totalCount} 집계 — /events(Page<>)와 분리된 별도 엔드포인트.
  // 목록과 동일한 기간·카테고리·국가로 호출(importance는 이 엔드포인트에 보내지 않음 — total이
  // 중요도 필터와 무관함을 API 시그니처로 보장).
  const { data: dailyCounts, isLoading: isDailyCountsLoading } = useFinancialCalendarDailyCountsQuery(filter);

  // TRAD-2071: impacts가 VALID_IMPACTS 전체와 다르면(기본값 HIGH만 포함) 숨겨진 이벤트가 있을 수 있음.
  const isImpactFiltered = !isSameImpactSet(filter.impacts, VALID_IMPACTS);

  // TRAD-2071: 고지("전체 M건 보기")는 이 티켓의 핵심 요구사항 — 연동이 깨져도 화면은 조용히 폴백하지만
  // (shouldShowDailyCountNotice가 undefined/빈 배열을 안전 처리), 그 무음 폴백 자체가 배포 후 연동 누락을
  // 가리는 사고로 이어질 수 있어 dev 환경에서만 콘솔 경고로 드러낸다.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isImpactFiltered || isDailyCountsLoading || grouped.length === 0) return;
    if (dailyCounts && dailyCounts.length > 0) return;
    console.warn(
      '[financial-calendar] daily-counts 연동이 비어있어 "전체 M건 보기" 고지가 렌더되지 않습니다. ' +
        'GET /v1/financial-calendar/events/daily-counts 연동을 확인하세요(TRAD-2071).',
      { from: filter.from, to: filter.to, categories: filter.categories, countries: filter.countries },
    );
  }, [
    isImpactFiltered,
    isDailyCountsLoading,
    grouped.length,
    dailyCounts,
    filter.from,
    filter.to,
    filter.categories,
    filter.countries,
  ]);

  // Intersection Observer for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // 전부 활성 = pristine(필터 없음). 하나라도 비활성이면 필터 적용 중.
  // TRAD-2071: impacts의 pristine 기준은 DEFAULT_IMPACTS(HIGH만) — VALID_IMPACTS 전체가 아니다.
  // TRAD-2831 — 판정을 model/utils 로 꺼냈다(주간·월간 빈 상태와 같은 함수).
  // TRAD-3725 — 기준은 화면이 정한다(일자 페이지 = 전체 중요도).
  const hasFilter = hasActiveCalendarFilter(filter, pristineImpacts);

  const handleReset = () => {
    // Notify parent via a pseudo-filter reset — parent manages URL state
    // We emit an event via custom event for simplicity (parent listens)
    window.dispatchEvent(new CustomEvent('financial-calendar:filter-reset'));
  };

  // 하나라도 전부 해제 = enabled: false 이지만 TanStack Query는 캐시 유지.
  // 컴포넌트 레벨에서 직접 빈 상태를 렌더해 캐시 데이터가 보이는 것을 차단.
  // TRAD-2831 — 종전엔 이 상태(조회를 안 함)를 "필터 조건에 맞는 이벤트가 없습니다"(조회 결과 0건)로
  // 말했다. 주간·월간 뷰와 같은 컴포넌트로 «어느 축이 비었는지»를 말한다.
  if (getEmptyFilterAxes(filter).length > 0) return <CalendarFilterAxisEmpty filter={filter} onReset={handleReset} />;

  if (errorDisplay === 'blocking') return <ErrorState onRetry={refetch} size="md" />;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonEventCard key={i} />
        ))}
      </div>
    );
  }

  if (grouped.length === 0) {
    // TRAD-2334 — 0건인데 중요도 필터만 풀면 볼 게 있는가?
    // 🔴 판정은 날짜 헤더 고지와 **같은 순수 함수**(`shouldShowDailyCountNotice`)를 쓴다.
    // 헤더는 날짜별로 묻고 여기서는 기간 전체로 묻는 것뿐이라, 조건을 따로 복사하면
    // "헤더는 더 있다는데 빈 화면은 없다고 하는" 상태가 생긴다.
    // 여기가 0건 분기라 `shownCount` 는 0 이다.
    // TRAD-2831 — 같은 판정을 model/utils `resolveExpandableTotal` 로 꺼냈다(주간·월간 빈 상태와 공용).
    return (
      <CalendarEmptyState
        hasFilter={hasFilter}
        onReset={handleReset}
        expandableTotal={resolveExpandableTotal(filter, dailyCounts)}
        onExpandToAll={onExpandToAll}
        periodTitleKey={emptyPeriodTitleKey}
      />
    );
  }

  return (
    <div className="space-y-1">
      {grouped.map(({ date, events }) => {
        const dayCount = findDailyCount(dailyCounts, date);
        return (
          <div key={date}>
            <DateGroupHeader
              date={date}
              // TRAD-3628 — n 은 «선택 중요도의 서버 집계 합»(로드 상태 무관). 페이지 경계에 걸린 날짜에서
              // events.length 는 다음 페이지 전까지 덜 센다. 옛 서버(medium/low 없음)면 종전대로 로드 수.
              shownCount={selectedImpactDailyCount(dayCount, filter.impacts) ?? events.length}
              totalCount={dayCount?.totalCount}
              isFiltered={isImpactFiltered}
              onExpandToAll={onExpandToAll}
            />
            <div className="space-y-2 py-2">
              {events.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  isSelected={selectedEventId === event.id}
                  onClick={onEventSelect}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-4" />

      {isFetchingNextPage && (
        <div className="space-y-2 pt-2">
          <SkeletonEventCard />
          <SkeletonEventCard />
        </div>
      )}

      {!hasNextPage && grouped.length > 0 && (
        <p className="text-center text-xs text-muted-foreground py-4">{t('financial_calendar.list.all_loaded')}</p>
      )}
    </div>
  );
};
